import { useState, useCallback, useRef } from "react";
import {
  CreditCard,
  Banknote,
  Smartphone,
  Building2,
  FileText,
  Pencil,
  Trash2,
  Check,
  X,
  Loader2,
  ExternalLink,
  Wallet,
  Download,
  Upload,
  FileCheck,
  Paperclip,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import BankNameInput from "./BankNameInput";
import IssuedReceiptButtons from "./IssuedReceiptButtons";
import type { PaymentEntry } from "../mockData";
import {
  useUpdatePayment,
  useDeletePayment,
} from "@/features/leads/api/usePaymentMutations";
import { useDocumentMutaion } from "@/hooks/api/useDocmentMutation";
import axiosInstance from "@/lib/axios";
import { doc_endpoint } from "@/config/endpoints";
import { toast } from "sonner";

const METHOD_CONFIG: Record<
  string,
  { icon: typeof CreditCard; color: string }
> = {
  UPI: { icon: Smartphone, color: "#6366f1" },
  "Bank Transfer": { icon: Building2, color: "#0ea5e9" },
  Cash: { icon: Banknote, color: "#22c55e" },
  Card: { icon: CreditCard, color: "#f59e0b" },
  Cheque: { icon: FileText, color: "#8b5cf6" },
  Advance: { icon: Wallet, color: "#0369a1" },
};

const PAYMENT_METHODS = [
  "UPI",
  "Bank Transfer",
  "Cash",
  "Card",
  "Cheque",
] as const;

function formatDateTime(dateStr: string) {
  return new Date(dateStr).toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmt(n: number) {
  return n.toLocaleString("en-IN");
}

function toDatetimeLocal(iso: string) {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function PaymentHistoryTimeline({
  payments,
  dealId,
  usedBankNames = [],
}: {
  payments: PaymentEntry[];
  dealId: string;
  usedBankNames?: string[];
}) {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editAmount, setEditAmount] = useState("");
  const [editMethod, setEditMethod] = useState("");
  const [editPaidAt, setEditPaidAt] = useState("");
  const [editBankName, setEditBankName] = useState("");
  const [editNote, setEditNote] = useState("");
  const [editReceiptDocId, setEditReceiptDocId] = useState<number | null>(null);
  const [editReceiptName, setEditReceiptName] = useState("");
  const [editUploadingReceipt, setEditUploadingReceipt] = useState(false);
  const editFileRef = useRef<HTMLInputElement>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [fetchingReceiptId, setFetchingReceiptId] = useState<number | null>(
    null
  );

  const updatePayment = useUpdatePayment();
  const deletePayment = useDeletePayment();
  const { uploadFile } = useDocumentMutaion();

  const fetchReceiptDoc = useCallback(
    async (
      docId: number
    ): Promise<{ fileUrl: string; fileName: string } | null> => {
      try {
        const res = await axiosInstance.get(doc_endpoint);
        const docs = res.data?.data ?? res.data?.documents ?? res.data ?? [];
        const doc = Array.isArray(docs)
          ? docs.find((d: any) => d.id === docId)
          : null;
        if (!doc?.fileUrl) return null;
        return {
          fileUrl: doc.fileUrl,
          fileName: doc.fileName || doc.name || "receipt",
        };
      } catch {
        toast.error("Failed to fetch receipt");
        return null;
      }
    },
    []
  );

  const handleViewReceipt = useCallback(
    async (docId: number) => {
      setFetchingReceiptId(docId);
      const doc = await fetchReceiptDoc(docId);
      setFetchingReceiptId(null);
      if (doc) window.open(doc.fileUrl, "_blank");
    },
    [fetchReceiptDoc]
  );

  const handleDownloadReceipt = useCallback(
    async (docId: number) => {
      setFetchingReceiptId(docId);
      const doc = await fetchReceiptDoc(docId);
      setFetchingReceiptId(null);
      if (doc) {
        try {
          // Stream via our origin instead of DO Spaces directly — Spaces has
          // no CORS policy for tenant hosts, so cross-origin `fetch` of the
          // public fileUrl fails. The proxy is tenant-scoped server-side.
          const response = await axiosInstance.get(
            `/core/doc/${docId}/download`,
            { responseType: "blob" }
          );
          const blobUrl = URL.createObjectURL(response.data as Blob);
          const a = document.createElement("a");
          a.href = blobUrl;
          a.download = doc.fileName;
          a.click();
          URL.revokeObjectURL(blobUrl);
        } catch {
          window.open(doc.fileUrl, "_blank");
        }
      }
    },
    [fetchReceiptDoc]
  );

  if (!payments.length) {
    return (
      <p className="text-[11px] text-[#9ba3a0] italic py-1.5">
        No payments recorded yet.
      </p>
    );
  }

  const sorted = [...payments].sort(
    (a, b) => new Date(b.paidAt).getTime() - new Date(a.paidAt).getTime()
  );

  const startEdit = (p: PaymentEntry) => {
    setEditingId(p.id);
    setEditAmount(String(p.amount));
    setEditMethod(p.method);
    setEditPaidAt(toDatetimeLocal(p.paidAt));
    setEditBankName(p.txnId ?? "");
    setEditNote(p.note ?? "");
    setEditReceiptDocId(p.receiptDocId ?? null);
    setEditReceiptName(p.receiptDocId ? "Existing receipt" : "");
  };

  const handleEditReceiptUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setEditUploadingReceipt(true);
    setEditReceiptName(file.name);
    uploadFile.mutate(
      {
        file,
        attachedTo: "child",
        attachedScope: dealId,
        category: "payment",
        name: file.name,
        attachmentLabel: "PAYMENT_RECEIPT",
      },
      {
        onSuccess: (data: any) => {
          const docId = data?.data?.id ?? data?.document?.id ?? data?.id;
          if (docId) setEditReceiptDocId(docId);
          setEditUploadingReceipt(false);
        },
        onError: () => {
          setEditReceiptName("");
          setEditUploadingReceipt(false);
        },
      }
    );
    if (editFileRef.current) editFileRef.current.value = "";
  };

  const handleUpdate = (paymentId: number) => {
    const amt = parseFloat(editAmount);
    if (!amt || amt <= 0) return;
    updatePayment.mutate(
      {
        id: paymentId,
        data: {
          amount: amt,
          method: editMethod,
          paidAt: editPaidAt ? new Date(editPaidAt).toISOString() : undefined,
          txnId: editBankName || undefined,
          note: editNote || undefined,
          receiptDocId: editReceiptDocId ?? undefined,
        },
      },
      { onSuccess: () => setEditingId(null) }
    );
  };

  const handleDelete = (paymentId: number) => {
    deletePayment.mutate(paymentId, {
      onSuccess: () => setConfirmDeleteId(null),
    });
  };

  return (
    <div className="flex flex-col gap-0">
      {sorted.map((p, idx) => {
        const isLast = idx === sorted.length - 1;
        const config = METHOD_CONFIG[p.method] || METHOD_CONFIG.Cash;
        const Icon = config.icon;
        const isEditing = editingId === p.id;
        const isDeleting = confirmDeleteId === p.id;
        const isAdvance = p.method === "Advance";

        if (isEditing) {
          return (
            <div key={p.id} className="flex items-stretch gap-2">
              <div className="flex flex-col items-center w-[18px] shrink-0">
                <div
                  className="w-[20px] h-[20px] rounded-full flex items-center justify-center shrink-0 mt-0.5"
                  style={{ backgroundColor: `${config.color}14` }}
                >
                  <Icon
                    className="size-2.5"
                    style={{ color: config.color }}
                    strokeWidth={2}
                  />
                </div>
                {!isLast && (
                  <div className="w-[1px] flex-1 bg-[#e8eae9] my-0.5" />
                )}
              </div>
              <div className={`flex-1 min-w-0 ${isLast ? "pb-0" : "pb-2.5"}`}>
                <div className="p-2 rounded-[6px] border border-[#c1e1d5] bg-[#fafbfa] flex flex-col gap-1.5">
                  <div className="grid grid-cols-2 gap-1.5">
                    <input
                      type="number"
                      value={editAmount}
                      onChange={(e) => setEditAmount(e.target.value)}
                      className="h-6 px-2 text-[11px] rounded-[5px] border border-[#e2e4e3] bg-white"
                      placeholder="Amount"
                    />
                    <select
                      value={editMethod}
                      onChange={(e) => setEditMethod(e.target.value)}
                      className="h-6 px-2 text-[11px] rounded-[5px] border border-[#e2e4e3] bg-white"
                    >
                      {PAYMENT_METHODS.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    <input
                      type="datetime-local"
                      value={editPaidAt}
                      onChange={(e) => setEditPaidAt(e.target.value)}
                      className="h-6 px-2 text-[11px] rounded-[5px] border border-[#e2e4e3] bg-white"
                    />
                    <BankNameInput
                      value={editBankName}
                      onChange={setEditBankName}
                      usedBankNames={usedBankNames}
                      placeholder="Bank name"
                    />
                  </div>
                  <input
                    type="text"
                    value={editNote}
                    onChange={(e) => setEditNote(e.target.value)}
                    placeholder="Note"
                    className="h-6 px-2 text-[11px] rounded-[5px] border border-[#e2e4e3] bg-white"
                  />
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <input
                        ref={editFileRef}
                        type="file"
                        accept="image/*,.pdf"
                        onChange={handleEditReceiptUpload}
                        className="hidden"
                      />
                      {editReceiptDocId ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleViewReceipt(editReceiptDocId)}
                            className="flex items-center gap-1 h-5 px-1.5 text-[9px] font-medium rounded-[4px] border border-[#c1e1d5] bg-[#f0faf5] text-[#267355]"
                          >
                            <FileCheck className="size-2.5" />
                            {editReceiptName || "Receipt"}
                          </button>
                          <button
                            onClick={() => {
                              setEditReceiptDocId(null);
                              setEditReceiptName("");
                            }}
                            className="size-4 flex items-center justify-center rounded hover:bg-[#fee2e2] text-[#9ba3a0]"
                          >
                            <X className="size-2.5" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => editFileRef.current?.click()}
                          disabled={editUploadingReceipt}
                          className="flex items-center gap-1 h-5 px-1.5 text-[9px] font-medium rounded-[4px] border border-dashed border-[#c5c9c7] text-[#697571] hover:bg-[#f7f9f8] disabled:opacity-50"
                        >
                          {editUploadingReceipt ? (
                            <Loader2 className="size-2.5 animate-spin" />
                          ) : (
                            <Upload className="size-2.5" />
                          )}
                          Receipt
                        </button>
                      )}
                    </div>
                    <div className="flex gap-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingId(null)}
                        className="h-5 text-[10px] px-2 rounded-[5px] gap-1"
                      >
                        <X className="size-2.5" /> Cancel
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={updatePayment.isPending}
                        onClick={() => handleUpdate(p.id)}
                        className="h-5 text-[10px] px-2 rounded-[5px] gap-1"
                      >
                        {updatePayment.isPending ? (
                          <Loader2 className="size-2.5 animate-spin" />
                        ) : (
                          <Check className="size-2.5" />
                        )}
                        Save
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        }

        return (
          <div key={p.id} className="flex items-stretch gap-3 group">
            <div className="flex flex-col items-center w-[22px] shrink-0">
              <div
                className="w-[24px] h-[24px] rounded-full flex items-center justify-center shrink-0 mt-0.5"
                style={{ backgroundColor: `${config.color}14` }}
              >
                <Icon
                  className="size-3"
                  style={{ color: config.color }}
                  strokeWidth={2}
                />
              </div>
              {!isLast && <div className="w-[1px] flex-1 bg-[#e8eae9] my-1" />}
            </div>

            <div className={`flex-1 min-w-0 ${isLast ? "pb-0" : "pb-3.5"}`}>
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[14px] font-bold text-[#1e302a] tabular-nums">
                  +{fmt(p.amount)}
                </span>
                <div className="flex items-center gap-2">
                  {!isAdvance && !isDeleting && (
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => startEdit(p)}
                        className="size-6 flex items-center justify-center rounded-md hover:bg-[#f0f1f0] text-[#9ba3a0] hover:text-[#374741] transition-colors"
                      >
                        <Pencil className="size-3" />
                      </button>
                      <button
                        onClick={() => setConfirmDeleteId(p.id)}
                        className="size-6 flex items-center justify-center rounded-md hover:bg-[#fee2e2] text-[#9ba3a0] hover:text-[#991b1b] transition-colors"
                      >
                        <Trash2 className="size-3" />
                      </button>
                    </div>
                  )}
                  <span className="text-[11px] text-[#9ba3a0] shrink-0">
                    {formatDateTime(p.paidAt)}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="text-[12px] text-[#697571] font-medium">
                  {p.method}
                </span>
                {p.txnId && (
                  <>
                    <span className="text-[10px] text-[#d4d8d6]">|</span>
                    <span className="text-[12px] text-[#697571] flex items-center gap-1">
                      <Building2 className="size-3 text-[#9ba3a0]" />
                      {p.txnId}
                    </span>
                  </>
                )}
              </div>
              {p.note && (
                <p className="text-[12px] text-[#8a9590] mt-1 italic">
                  {p.note}
                </p>
              )}
              {p.receiptDocId && (
                <div className="flex items-center gap-1.5 mt-1.5">
                  <div className="flex items-center gap-1 h-6 px-2 rounded-md bg-[#f0f5ff] border border-[#d0e0f5] text-[#4a7cc9]">
                    <Paperclip className="size-3" />
                    <span className="text-[10px] font-medium">Receipt</span>
                  </div>
                  <button
                    onClick={() => handleViewReceipt(p.receiptDocId!)}
                    disabled={fetchingReceiptId === p.receiptDocId}
                    className="size-6 flex items-center justify-center rounded-md border border-[#d0e0f5] bg-white text-[#4a7cc9] hover:bg-[#f0f5ff] transition-colors disabled:opacity-50"
                    title="View receipt"
                  >
                    {fetchingReceiptId === p.receiptDocId ? (
                      <Loader2 className="size-3 animate-spin" />
                    ) : (
                      <ExternalLink className="size-3" />
                    )}
                  </button>
                  <button
                    onClick={() => handleDownloadReceipt(p.receiptDocId!)}
                    disabled={fetchingReceiptId === p.receiptDocId}
                    className="size-6 flex items-center justify-center rounded-md border border-[#c1e1d5] bg-white text-[#267355] hover:bg-[#f0faf5] transition-colors disabled:opacity-50"
                    title="Download receipt"
                  >
                    <Download className="size-3" />
                  </button>
                </div>
              )}
              {!p.receiptDocId && (
                <span className="text-[10px] text-[#c5c9c7] italic mt-1 inline-block">
                  No receipt attached
                </span>
              )}
              {!isAdvance && p.id > 0 && String(p.method) !== "Discount" && !isDeleting && (
                <IssuedReceiptButtons paymentId={p.id} />
              )}

              {isDeleting && (
                <div className="mt-2 flex items-center gap-2.5 p-2 rounded-[8px] bg-[#fff5f5] border border-[#fecaca]">
                  <span className="text-[12px] text-[#991b1b] font-medium">
                    Delete this payment?
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setConfirmDeleteId(null)}
                    className="h-7 text-[11px] px-3 rounded-[6px]"
                  >
                    No
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={deletePayment.isPending}
                    onClick={() => handleDelete(p.id)}
                    className="h-7 text-[11px] px-3 rounded-[6px] bg-[#991b1b] hover:bg-[#7f1d1d]"
                  >
                    {deletePayment.isPending ? (
                      <Loader2 className="size-3 animate-spin" />
                    ) : (
                      "Yes, Delete"
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
